<template>
  <el-dialog
    class="member-recharge-dialog"
    :visible="visible"
    :before-close="handleClose"
    @open="handleOpen"
  >
    <div slot="title" class="recharge-header">
      <img class="member-avatar" :src="avatarUrl" alt="用户头像" />
      <div class="member-user-copy">
        <strong>{{ currentUser.nickName || "用户" }}</strong>
        <p>
          <span :class="{ active: memberInfo.active === true }">
            {{ memberInfo.active === true ? "会员生效中" : "未开通" }}
          </span>
          <em>{{ membershipStatusText }}</em>
        </p>
      </div>
    </div>

    <div class="recharge-body">
      <div class="member-type-title">
        <img :src="priceIcon" alt="" /><strong>个人会员</strong>
      </div>
      <div class="plan-list" v-loading="plansLoading">
        <div
          v-for="(plan, index) in displayPlans"
          :key="plan.code"
          type="button"
          class="plan-item"
          :class="{
            active: selectedPlanCode === plan.code,
            recommended: index === 0,
            generating: orderLoading,
          }"
          :aria-disabled="orderLoading ? 'true' : 'false'"
          @click="handleSelectPlan(plan.code)"
        >
          <span v-if="index === 0" class="recommend-tag"
            ><img :src="hotIcon" alt="" />爆款推荐!</span
          >
          <span class="plan-content">
            <span class="plan-name">{{ plan.name }}</span>
            <span class="plan-price"
              ><small>¥</small>{{ formatPrice(plan) }}</span
            >
            <span class="plan-average"
              >每月约{{ monthlyAverage(plan) }}元</span
            >
          </span>
          <span v-if="index === 0" class="plan-popularity">80%的用户选择</span>
        </div>
      </div>
      <div class="redeem-code">
        <div class="member-type-title">
          <img :src="redeemIcon" alt="" /><strong>兑换码</strong>
        </div>
        <div class="redeem-code__input">
          <el-input
            v-model="inputValue"
            size="small"
            maxlength="19"
            placeholder="请输入兑换码"
            @input="handleRedeemInput"
            @keyup.enter.native="handleRedeem"
          />
          <button
            type="button"
            :disabled="redeemLoading"
            @click="handleRedeem"
          >
            {{ redeemLoading ? "兑换中" : "兑换" }}
          </button>
        </div>
      </div>
      <div class="pay-content" v-loading="orderLoading">
        <div class="qrcode-box" :class="{ expired: isOrderUnavailable }">
          <canvas v-show="orderInfo.codeUrl" ref="qrcodeCanvas"></canvas>
          <div
            v-if="!orderInfo.codeUrl && !orderLoading"
            class="qrcode-placeholder"
          >
            选择套餐后生成支付二维码
          </div>
          <div v-if="isOrderUnavailable" class="qrcode-mask">
            <strong>{{ orderUnavailableText }}</strong>
            <el-button
              type="text"
              :loading="orderLoading"
              @click.stop="handleCreateOrder"
              >刷新二维码
            </el-button>
          </div>
        </div>
        <div class="pay-detail">
          <div class="detail-row amount-row">
            <span class="label">实付金额：</span
            ><strong><small>¥</small>{{ payAmount }}</strong>
          </div>
          <div class="detail-row">
            <span class="label">开通会员：</span
            ><em>{{ selectedPlanName }}</em>
          </div>
          <div class="detail-row">
            <span class="label">到期时间：</span><em>{{ membershipValidUntil }}</em>
          </div>
          <div class="detail-row">
            <span class="label">开通账号：</span
            ><em class="account" :title="accountName">{{ accountName }}</em>
          </div>
        </div>
        <button
          type="button"
          class="contact-button"
          @click="contactVisible = true"
        >
          <img :src="contactButtonIcon" alt="" />联系客服
        </button>
        <div v-if="remainingSeconds > 0" class="pay-countdown">
          二维码有效期 {{ formatCountdown(remainingSeconds) }}
        </div>
        <div v-else-if="isOrderExpired" class="pay-countdown expired">
          二维码已失效，请刷新后重新扫码
        </div>
      </div>
      <div class="pay-footer">
        <div class="wechat-tip">
          <img :src="wechatPayIcon" alt="" />请使用微信扫码支付
        </div>
        <!--<div class="agreement-tip">-->
        <!--  支付即表示您已阅读同意<a href="javascript:void(0)">《飞译会员服务协议》</a>-->
        <!--</div>-->
      </div>
    </div>
    <MemberContactDialog
      :visible.sync="contactVisible"
      :qr-code="contactQrCode"
    />
    <MemberRedeemResultDialog
      :key="redeemResultKey"
      :visible.sync="redeemResultVisible"
      :status="redeemResult.status"
      :message="redeemResult.message"
      @view-history="$emit('open-history')"
    />
  </el-dialog>
</template>

<script>
import { mapActions } from "vuex";
import {
  queryMemberPlans,
  createWechatNativeOrder,
  queryMemberOrder,
  redeemMemberCard,
} from "@/services/member";
import { drawQrCode } from "@/utils/qrcode";
import MemberContactDialog from "./components/MemberContactDialog";
import MemberRedeemResultDialog from "./components/MemberRedeemResultDialog";

export default {
  name: "MemberRechargeDialog",
  components: { MemberContactDialog, MemberRedeemResultDialog },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: "",
      redeemLoading: false,
      redeemRequestId: 0,
      redeemResultKey: 0,
      redeemResultVisible: false,
      redeemResult: {
        status: "DISABLED",
        message: "",
      },
      planList: [],
      selectedPlanCode: "",
      plansLoading: false,
      orderLoading: false,
      orderInfo: {},
      contactVisible: false,
      pollingTimer: null,
      countdownTimer: null,
      remainingSeconds: 0,
      orderExpireTimestamp: 0,
      orderRequestId: 0,
      plansRequestId: 0,
      priceIcon: require("@/assets/imgs/payment/price-icon.png"),
      redeemIcon: require("@/assets/imgs/payment/redeem-icon.png"),
      hotIcon: require("@/assets/imgs/payment/hot-icon.png"),
      wechatPayIcon: require("@/assets/imgs/payment/wechat-pay.png"),
      contactButtonIcon: require("@/assets/imgs/payment/connect-button-icon.png"),
      defaultAvatar: require("@/components/layout/module/header/assets/images/user.png"),
    };
  },
  computed: {
    currentUser() {
      const storeUser = this.$store.getters["layout/getUserInfo"] || {};
      if (storeUser.id) return storeUser;
      try {
        return JSON.parse(
          localStorage.getItem("userInfo") ||
            localStorage.getItem("myUser") ||
            "{}"
        );
      } catch (e) {
        return {};
      }
    },
    avatarUrl() {
      return this.currentUser.avatar || this.defaultAvatar;
    },
    memberInfo() {
      return this.$store.getters["layout/getMemberInfo"] || {};
    },
    membershipStatusText() {
      if (this.memberInfo.active === true)
        return this.memberInfo.validUntil
          ? `有效期至${this.formatDate(
              this.parseDate(this.memberInfo.validUntil)
            )}`
          : "";
      return "开通会员尊享翻译权益";
    },
    contactQrCode() {
      return window.MEMBER_SERVICE_QR_CODE || "";
    },
    displayPlans() {
      return [...this.planList].sort(
        (a, b) => Number(b.durationMonths) - Number(a.durationMonths)
      );
    },
    selectedPlan() {
      return (
        this.planList.find((plan) => plan.code === this.selectedPlanCode) || {}
      );
    },
    payAmount() {
      return (Number(this.selectedPlan.priceFen) || 0) / 100;
    },
    selectedPlanName() {
      return this.selectedPlan.name || "--";
    },
    membershipValidUntil() {
      const months = Number(this.selectedPlan.durationMonths) || 0;
      if (!months) return "--";
      const currentMembershipValidUntil =
        this.memberInfo.active === true
          ? this.parseDate(this.memberInfo.validUntil)
          : null;
      const date =
        currentMembershipValidUntil ||
        this.parseDate(this.orderInfo.createdAt) ||
        new Date();
      const validUntil = new Date(date.getTime());
      validUntil.setMonth(validUntil.getMonth() + months);
      return this.formatDate(validUntil);
    },
    accountName() {
      const user = this.currentUser;
      return (
        user.loginPhone ||
        user.telephone ||
        user.phone ||
        user.mobile ||
        user.email ||
        user.loginName ||
        user.id ||
        "--"
      );
    },
    isOrderExpired() {
      return (
        !!this.orderInfo.codeUrl &&
        this.remainingSeconds === 0 &&
        !["PAID", "CLOSED", "FAILED"].includes(this.orderInfo.status)
      );
    },
    isOrderUnavailable() {
      return (
        this.isOrderExpired ||
        ["CLOSED", "FAILED"].includes(this.orderInfo.status)
      );
    },
    orderUnavailableText() {
      if (this.orderInfo.status === "CLOSED") return "订单已关闭";
      if (this.orderInfo.status === "FAILED") return "支付失败";
      return "二维码已失效";
    },
  },
  beforeDestroy() {
    this.clearOrderState();
  },
  methods: {
    ...mapActions("layout", ["checkMembership", "updateMembership"]),
    async handleOpen() {
      this.clearOrderState();
      this.selectedPlanCode = "";
      this.inputValue = "";
      this.redeemLoading = false;
      this.planList = [];
      const plansLoaded = await this.loadPlans();
      if (!plansLoaded || !this.visible) return;
      if (this.displayPlans.length) {
        this.selectedPlanCode = this.displayPlans[0].code;
        await this.handleCreateOrder();
      }
    },
    async loadPlans() {
      const requestId = ++this.plansRequestId;
      this.plansLoading = true;
      try {
        const response = await queryMemberPlans();
        if (requestId !== this.plansRequestId) return false;
        this.planList = Array.isArray(response)
          ? response
          : (response &&
              (response.records || response.list || response.data)) ||
            [];
        return true;
      } catch (e) {
        return false;
      } finally {
        if (requestId === this.plansRequestId) this.plansLoading = false;
      }
    },
    handleSelectPlan(code) {
      if (this.orderLoading) return;
      if (
        this.selectedPlanCode === code &&
        this.orderInfo.codeUrl &&
        !this.isOrderUnavailable
      )
        return;
      this.selectedPlanCode = code;
      this.clearOrderState();
      this.handleCreateOrder();
    },
    async handleCreateOrder() {
      if (
        this.orderLoading ||
        !this.selectedPlanCode ||
        !this.currentUser.id
      )
        return;
      const requestId = ++this.orderRequestId;
      this.orderLoading = true;
      this.clearTimers();
      this.orderInfo = {};
      try {
        const result = await createWechatNativeOrder({
          userId: String(this.currentUser.id),
          planCode: this.selectedPlanCode,
        });
        if (requestId !== this.orderRequestId) return;
        this.orderInfo = result || {};
        this.$nextTick(() => {
          if (this.$refs.qrcodeCanvas && this.orderInfo.codeUrl)
            drawQrCode(this.$refs.qrcodeCanvas, this.orderInfo.codeUrl, 150);
        });
        this.syncExpireTimestamp();
        this.startCountdown();
        this.startPolling();
      } finally {
        if (requestId === this.orderRequestId) this.orderLoading = false;
      }
    },
    startPolling() {
      if (!this.orderInfo.orderId) return;
      this.pollingTimer = window.setInterval(this.queryOrderStatus, 3000);
      this.queryOrderStatus();
    },
    async queryOrderStatus() {
      const orderId = this.orderInfo.orderId;
      if (!orderId) return;
      try {
        const result = await queryMemberOrder(orderId);
        if (this.orderInfo.orderId !== orderId) return;
        this.orderInfo = {
          ...this.orderInfo,
          ...result,
          codeUrl: result.codeUrl || this.orderInfo.codeUrl,
        };
        this.syncExpireTimestamp();
        this.updateCountdown();
        if (result.status === "PAID") {
          this.clearTimers();
          await this.checkMembership(String(this.currentUser.id));
          this.$message.success("支付成功，会员已开通");
          this.$emit("paid");
        } else if (
          ["CLOSED", "FAILED"].includes(result.status) ||
          this.isOrderExpired
        ) {
          this.clearTimers();
        }
      } catch (e) {
        // Preserve the current QR code when a status query fails temporarily.
      }
    },
    syncExpireTimestamp() {
      const expireDate = this.parseDate(this.orderInfo.expireAt);
      if (expireDate) {
        this.orderExpireTimestamp = expireDate.getTime();
        return;
      }
      const seconds = Number(this.orderInfo.remainingSeconds);
      this.orderExpireTimestamp =
        Number.isFinite(seconds) && seconds > 0
          ? Date.now() + seconds * 1000
          : 0;
    },
    startCountdown() {
      this.updateCountdown();
      if (this.remainingSeconds > 0)
        this.countdownTimer = window.setInterval(this.updateCountdown, 1000);
    },
    updateCountdown() {
      this.remainingSeconds = this.orderExpireTimestamp
        ? Math.max(
            0,
            Math.floor((this.orderExpireTimestamp - Date.now()) / 1000)
          )
        : 0;
      if (this.remainingSeconds === 0 && this.orderInfo.codeUrl)
        this.clearTimers();
    },
    clearOrderState() {
      this.orderRequestId += 1;
      this.clearTimers();
      this.orderInfo = {};
      this.orderLoading = false;
      this.remainingSeconds = 0;
      this.orderExpireTimestamp = 0;
    },
    clearTimers() {
      if (this.pollingTimer) clearInterval(this.pollingTimer);
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      this.pollingTimer = null;
      this.countdownTimer = null;
    },
    handleClose() {
      this.plansRequestId += 1;
      this.plansLoading = false;
      this.redeemRequestId += 1;
      this.redeemLoading = false;
      this.clearOrderState();
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    monthlyAverage(plan) {
      const months = Number(plan.durationMonths) || 1;
      return ((Number(plan.priceFen) || 0) / 100 / months).toFixed(2);
    },
    formatPrice(plan) {
      return (Number(plan.priceFen) || 0) / 100;
    },
    formatCountdown(seconds) {
      const minute = Math.floor(seconds / 60);
      const second = seconds % 60;
      return `${minute}:${second < 10 ? "0" : ""}${second}`;
    },
    parseDate(value) {
      if (!value) return null;
      const date = new Date(String(value).replace(/-/g, "/").replace("T", " "));
      return Number.isFinite(date.getTime()) ? date : null;
    },
    formatDate(date) {
      if (!date) return "--";
      const pad = (number) => String(number).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}`;
    },
    handleRedeemInput(value) {
      const cardCharacters = String(value || "")
        .replace(/-/g, "")
        .replace(/\s+/g, "")
        .slice(0, 16);
      this.inputValue = (cardCharacters.match(/.{1,4}/g) || []).join("-");
    },
    normalizeRedeemStatus(code) {
      const normalizedCode = String(code || "").toUpperCase();
      if (normalizedCode === "SUCCESS") return "SUCCESS";
      if (normalizedCode === "CARD_EXPIRED") return "EXPIRED";
      if (normalizedCode === "CARD_ALREADY_REDEEMED") return "USED";
      if (normalizedCode === "CARD_DISABLED") return "DISABLED";
      return "";
    },
    showRedeemResult(result = {}, status) {
      this.redeemResult = {
        status,
        message: result.message || "",
      };
      this.redeemResultKey += 1;
      this.redeemResultVisible = true;
    },
    async handleRedeemResult(result = {}) {
      const code = String(result.code || result.status || "").toUpperCase();
      if (code === "INVALID_CARD") {
        this.$message.warning(result.message || "兑换码格式错误，请确认");
        return;
      }
      const status = this.normalizeRedeemStatus(code);
      if (!status) {
        this.$message.error(result.message || "兑换失败，请稍后重试");
        return;
      }
      if (status === "SUCCESS") {
        if (result.membership) this.updateMembership(result.membership);
        else await this.checkMembership(String(this.currentUser.id));
        this.handleClose();
        this.$emit("redeemed", result);
      }
      this.showRedeemResult(result, status);
    },
    async handleRedeem() {
      if (this.redeemLoading) return;
      const cardCode = this.inputValue.trim();
      if (!cardCode) {
        this.$message.warning("请输入兑换码");
        return;
      }
      if (!this.currentUser.id) {
        this.$message.error("未获取到当前用户信息，请重新登录后再试");
        return;
      }
      const requestId = ++this.redeemRequestId;
      this.redeemLoading = true;
      try {
        const result = await redeemMemberCard({
          userId: String(this.currentUser.id),
          cardCode,
        });
        if (requestId !== this.redeemRequestId) return;
        await this.handleRedeemResult(result);
      } catch (error) {
        if (requestId !== this.redeemRequestId) return;
        const result =
          (error && error.response && error.response.data) || {};
        await this.handleRedeemResult(result);
      } finally {
        if (requestId === this.redeemRequestId) this.redeemLoading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.member-recharge-dialog {
  /deep/ .el-dialog {
    width: 800px;
    margin: 0 !important;
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 12px;
    overflow: hidden;
    transform: translate(-50%, -50%);
    box-shadow: 0 18px 52px rgba(22, 41, 78, 0.25);
  }
  /deep/ .el-dialog__header {
    height: 94px;
    padding: 0 24px;
    box-sizing: border-box;
    background: url(~@/assets/imgs/payment/dialog-header-bg.png) no-repeat center / 106% 100%;
  }
  /deep/ .el-dialog__headerbtn {
    z-index: 2;
    top: 18px;
    right: 22px;
    .el-dialog__close {
      color: #fff;
      font-size: 21px;
    }
  }
  /deep/ .el-dialog__body {
    padding: 0;
  }
}
.recharge-header {
  position: relative;
  z-index: 1;
  height: 92px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.member-avatar {
  width: 42px;
  height: 42px;
  border: 2px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  object-fit: cover;
}
.member-user-copy {

  text-align: left;
  strong {
    color: #fff;
    font-size: 18px;
    line-height: 22px;
  }

  p {
    margin: 2px 0 0;
    display: flex;
    gap: 8px;
    font-size: 11px;
    line-height: 17px;
  }

  span {
    color: #d9ecff;
    &.active {
      color: #80ffbf;
    }
  }

  em {
    color: #ffdf83;
    font-style: normal;
  }
}

.recharge-body {
  position: relative;
  padding: 0 24px;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    top: -15px;
    left: 0;
    width: 100%;
    height: 16px;
    border-radius: 18px 18px 0 0;
    background-color: #ffffff;
  }
}

.member-type-title {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #30394e;
  img {
    width: 18px;
    height: 18px;
  }
  strong {
    font-size: 16px;
  }
}

.redeem-code {
  margin-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E9EDF2;

  .redeem-code__input {
    display: flex;

    .el-input {
      flex: 1;

      ::v-deep .el-input__inner {
        height: 36px;
        background-color: #F7F8FC;
        border: none;
      }
    }

    button {
      margin-left: 12px;
      padding: 0;
      width: 60px;
      height: 36px;
      line-height: 36px;
      outline: none;
      background: linear-gradient( 136deg, #0269FF 0%, #00C6FF 100%);
      border-radius: 4px;
      color: #ffffff;
      opacity: 1;
      transition: opacity 0.1s ease-in-out;
      border: 0;
      cursor: pointer;

      &:hover {
        opacity: 0.8;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
    }
  }
}
.plan-list {
  height: 180px;
  margin-top: 10px;
  display: flex;
  gap: 16px;
}
.plan-item {
  box-sizing: border-box;
  position: relative;
  isolation: isolate;
  flex: 0 0 182px;
  height: 180px;
  padding: 0;
  border: 2px solid #dfe5ed;
  border-radius: 18px;
  background: #f9f9f9;
  color: #202839;
  text-align: center;
  cursor: pointer;
  outline: none;

  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    inset: 0;
    border-radius: 18px;
    background: url(~@/assets/imgs/payment/basic-bg.png) no-repeat center / 100% 100%;
    pointer-events: none;
  }
}
.plan-item.generating {
  cursor: wait;
}
.plan-item.active {
  padding: 2px 2px 0;
  border: 0;
  background: linear-gradient(130deg, #0269ff, #00c6ff);
}
.plan-item.active::before {
  background-image: url(~@/assets/imgs/payment/basic-bg-active.png);
}
.plan-item.recommended::before {
  background-image: url(~@/assets/imgs/payment/recommend-bg.png);
}
.plan-item.recommended.active {
  .plan-popularity {
    color: #ffffff;
  }
  &::before {
    background-image: url(~@/assets/imgs/payment/recommend-bg-active.png);
  }
}
.plan-item.recommended {
  flex-basis: 352px;
}
.recommend-tag {
  position: absolute;
  z-index: 3;
  top: -13px;
  left: 0;
  height: 26px;
  padding: 0 12px;
  border-radius: 9px 0 9px 0;
  display: flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(90deg, #ff7417, #ffb01e);
  color: #fff;
  font-size: 11px;
  img {
    width: 16px;
    height: 16px;
  }
}
.plan-content,
.plan-name,
.plan-price,
.plan-average,
.plan-popularity {
  display: block;
}
.plan-content {
  position: relative;
  z-index: 2;
  height: 150px;
  margin: 0;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background: #ffffff;
}
.plan-name {
  font-size: 18px;
  line-height: 24px;
}
.plan-price {
  margin: 4px 0;
  font-size: 38px;
  small {
    margin-right: 4px;
    font-size: 16px;
  }
}
.plan-average {
  height: 26px;
  line-height: 26px;
  padding: 0 16px;
  background-color: #F8F9FC;
  font-size: 12px;
  border-radius: 13px;
  color: #999999;
}
.plan-popularity {
  line-height: 28px;
  font-size: 12px;
  color: #0078fc;
}
.pay-content {
  position: relative;
  height: 160px;
  margin: 16px 0 8px;
  border-radius: 18px;
  display: flex;
  align-items: flex-start;
  gap: 22px;
  box-sizing: border-box;
  background: #F9FBFF;
}
.qrcode-box {
  position: relative;
  width: 160px;
  height: 160px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 0 20px 0 rgba(0,0,0,0.05);
  canvas {
    width: 150px !important;
    height: 150px !important;
  }
  &.expired canvas {
    opacity: 0.18;
  }
}
.qrcode-placeholder {
  width: 110px;
  color: #b5bbc6;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.qrcode-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  color: #687187;
  font-size: 12px;
}
.pay-detail {
  width: 286px;
  padding-top: 8px;
}
.detail-row {
  min-height: 29px;
  display: flex;
  align-items: center;
  color: #999999;
  font-size: 14px;

  .label {
    width: 80px;
    text-align: left;
  }

  .account {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  em {
    min-width: 0;
    color: #000000;
  }
}
.amount-row {
  min-height: 42px;
  strong {
    color: #FF0000;
    font-size: 32px;
  }

  small {
    margin-right: 3px;
    font-size: 13px;
  }
}
.contact-button {
  position: absolute;
  right: 16px;
  bottom: 12px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #5B92FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #333333;
  font-size: 16px;
  cursor: pointer;
  img {
    width: 24px;
    height: 24px;
  }
}
.pay-footer {
  padding-bottom: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #999999;
  font-size: 14px;
}
.wechat-tip {
  display: flex;
  align-items: center;
  gap: 5px;

  img {
    width: 18px;
    height: 18px;
  }
}
.agreement-tip {
  a {
    color: #0269FF;
    text-decoration: none;
  }
}
.pay-countdown {
  position: absolute;
  right: 16px;
  top: 16px;
  color: #98a0af;
  font-size: 12px;
  &.expired {
    color: #f56c6c;
  }
}
</style>
